import { AmenitiesList } from '@/components/amenities';
import { HotelAccessCodesList } from '@/components/hotel-access-codes/HotelAccessCodesList';
import { PromotionalOffersList } from '@/components/promotional-offers/PromotionalOffersList';
import { ToursList } from '@/components/tours';
import { amenityCrudConfig, type Amenity } from '@/config/objects/amenities';
import {
  hotelAccessCodeCrudConfig,
  type HotelAccessCode,
} from '@/config/objects/hotel-access-codes';
import { hotelCrudConfig, type Hotel } from '@/config/objects/hotels';
import {
  promotionalOfferCrudConfig,
  type PromotionalOffer,
} from '@/config/objects/promotional-offers';
import { tourCrudConfig, type Tour } from '@/config/objects/tours';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  useRequireOrganization,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Hotel detail page that works for both internal and non-internal organizations
 * Internal orgs can edit/delete hotels and manage tours
 * Non-internal orgs have read-only access to hotel info and tours
 */
export function HotelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { isInternal } = useRequireOrganization();
  const [hotel, setHotel] = React.useState<Hotel | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (hotelId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/hotels/${hotelId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate hotels list to refresh data
        queryClient.invalidateQueries({ queryKey: ['Hotel'] });
        navigate('/hotels');
      } else {
        throw new Error(response.error || 'Failed to delete hotel');
      }
    },
  });

  // Create custom config for hotel-specific tours
  // Internal orgs can add/remove tours, non-internal orgs can only view
  const hotelToursConfig: CrudConfig<Tour> = useMemo(
    () => ({
      ...tourCrudConfig,
      entityName: 'Tour',
      entityNamePlural: 'Tours',
      endpoints: {
        ...tourCrudConfig.endpoints,
        list: `/hotels/${id}/tours`,
        delete: (tourId: string) => `/hotels/${id}/tours/${tourId}`,
      },
      listView: {
        ...tourCrudConfig.listView,
        actions: {
          add: {
            enabled: isInternal,
            endpoint: '/tours',
            where: hotel?.greaterAreaId
              ? {
                  greaterAreaId: hotel.greaterAreaId,
                  hotels: {
                    none: {
                      hotelId: hotel.id,
                    },
                  },
                }
              : hotel
                ? {
                    hotels: {
                      none: {
                        hotelId: hotel.id,
                      },
                    },
                  }
                : undefined,
            onAdd: async (tour: Tour) => {
              await apiClient.post(`/hotels/${id}/tours`, { tourId: tour.id });
              toast.success('Tour added to hotel');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: isInternal,
            onRemove: async (tour: Tour) => {
              await apiClient.delete(`/hotels/${id}/tours/${tour.id}`);
              toast.success('Tour removed from hotel');
            },
          },
        },
      },
    }),
    [hotel, id, apiClient, isInternal],
  );

  // Create custom config for hotel-specific amenities
  const hotelAmenitiesConfig: CrudConfig<Amenity> = useMemo(
    () => ({
      ...amenityCrudConfig,
      entityName: 'Amenity',
      entityNamePlural: 'Amenities',
      endpoints: {
        ...amenityCrudConfig.endpoints,
        list: `/hotels/${id}/amenities`,
        delete: (amenityId: string) => `/hotels/${id}/amenities/${amenityId}`,
      },
      listView: {
        ...amenityCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/amenities',
            where: hotel
              ? {
                  hotelId: {
                    not: hotel.id,
                  },
                }
              : undefined,
            onAdd: async (amenity: Amenity) => {
              await apiClient.post(`/hotels/${id}/amenities`, { amenityId: amenity.id });
              toast.success('Amenity added to hotel');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (amenity: Amenity) => {
              await apiClient.delete(`/hotels/${id}/amenities/${amenity.id}`);
              toast.success('Amenity removed from hotel');
            },
          },
        },
      },
    }),
    [hotel, id, apiClient],
  );

  /**
   * Navigate to edit form for this hotel
   */
  const handleEdit = (hotel: Hotel) => {
    navigate(`/hotels/${hotel.id}/edit`);
  };

  /**
   * Handle hotel deletion
   */
  const handleDelete = async (hotel: Hotel) => {
    toast.promise(deleteMutation.mutateAsync(hotel.id), {
      loading: 'Deleting hotel...',
      success: 'Hotel deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete hotel',
    });
  };

  // Create custom config for hotel-specific promotional offers
  const hotelPromotionalOffersConfig: CrudConfig<PromotionalOffer> = useMemo(
    () => ({
      ...promotionalOfferCrudConfig,
      entityName: 'Promotional Offer',
      entityNamePlural: 'Promotional Offers',
      endpoints: {
        ...promotionalOfferCrudConfig.endpoints,
        list: `/hotels/${id}/promotional-offers`,
        delete: (promotionalOfferId: string) =>
          `/hotels/${id}/promotional-offers/${promotionalOfferId}`,
      },
      listView: {
        ...promotionalOfferCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/promotional-offers',
            // Exclude promotional offers already in this hotel
            where: hotel
              ? {
                  hotels: {
                    none: {
                      hotelId: hotel.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (promotionalOffer: PromotionalOffer) => {
              await apiClient.post(`/hotels/${id}/promotional-offers`, {
                promotionalOfferId: promotionalOffer.id,
              });
              toast.success('Promotional offer added to hotel');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (promotionalOffer: PromotionalOffer) => {
              await apiClient.delete(`/hotels/${id}/promotional-offers/${promotionalOffer.id}`);
              toast.success('Promotional offer removed from hotel');
            },
          },
        },
      },
    }),
    [hotel, id, apiClient],
  );

  // Create custom config for hotel-specific access codes
  const hotelAccessCodesConfig: CrudConfig<HotelAccessCode> = useMemo(
    () => ({
      ...hotelAccessCodeCrudConfig,
      entityName: 'Access Code',
      entityNamePlural: 'Access Codes',
      endpoints: {
        ...hotelAccessCodeCrudConfig.endpoints,
        list: `/hotels/${id}/access-codes`,
        delete: (accessCodeId: string) => `/hotels/${id}/access-codes/${accessCodeId}`,
      },
      listView: {
        ...hotelAccessCodeCrudConfig.listView,
        actions: {
          create: {
            enabled: true,
            onCreate: async (accessCodeData: Partial<HotelAccessCode>) => {
              await apiClient.post(`/hotels/${id}/access-codes`, accessCodeData);
              toast.success('Access code created for hotel');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (accessCode: HotelAccessCode) => {
              await apiClient.delete(`/hotels/${id}/access-codes/${accessCode.id}`);
              toast.success('Access code removed from hotel');
            },
          },
        },
      },
    }),
    [id, apiClient],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Hotel Not Found</h2>
          <p className="text-muted-foreground">The requested hotel could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Hotels', href: '/hotels' },
    { label: hotel?.name || 'Hotel', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={hotelCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={isInternal ? handleEdit : undefined}
          onDelete={isInternal ? handleDelete : undefined}
          onData={(hotelData: Hotel) => setHotel(hotelData)}
        />

        {/* Tours Section */}
        <ToursList config={hotelToursConfig} className="p-0" baseUrl="/tours" />

        {/* Amenities Section */}
        <AmenitiesList config={hotelAmenitiesConfig} className="p-0" baseUrl="/amenities" />

        {/* Promotional Offers Section */}
        <PromotionalOffersList
          config={hotelPromotionalOffersConfig}
          className="p-0"
          baseUrl="/promotional-offers"
        />

        {/* Access Codes Section */}
        <HotelAccessCodesList
          config={hotelAccessCodesConfig}
          className="p-0"
          baseUrl="/hotel-access-codes"
        />
      </div>
    </PageContainer>
  );
}
